import * as React from 'react';
import {ChangeEvent, ReactElement, useCallback, useEffect, useMemo, useRef, useState} from "react";

import useMediaQuery from "@mui/material/useMediaQuery";
import {Theme} from "@mui/material/styles/createTheme";
import useTheme from "@mui/material/styles/useTheme";
import makeStyles from "@mui/styles/makeStyles";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import {ModalProps} from "@mui/material/Modal/Modal";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import Toolbar from "@mui/material/Toolbar";
import {TransitionProps} from "@mui/material/transitions";

import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {ICredencial, ICredResponse} from "../api/ApiCredencial";
import Typography from "@mui/material/Typography";
import {Box, Button} from "@mui/material";
import {Credencial} from "./Credencial";
import {Download} from "@mui/icons-material";
import {toJpeg, toPng} from "html-to-image";
import download from "downloadjs";
import {isIos} from "../util/Browser";
import ExtratoTable from "./ExtratoTable";
import SaldoCard from "./SaldoCard";



interface ICredencialModal {
    credencial: ICredResponse,
    onClose: ModalProps['onClose'],
    children?: any
}

interface IModalView {
    onClose: ModalProps['onClose'],
    children: any
}

const styles: () => { [key: string]: string } = makeStyles((theme: Theme) => ({
    textLight: {
        color: theme.palette.common.white
    },

    closeBtn: {
        color: theme.palette.common.white,
        padding: "5px",
        float: "right"
    },
    title: {
        marginLeft: theme.spacing(2),
        flex: 1,
    },
    appBarContent: {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.primary.main
    },
    modalContent: {
        backgroundColor: theme.palette.primary.main
    },
    modalTitle: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
}));

const Transition = React.forwardRef(function Transition(
    {children, ...props}: TransitionProps, ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props}>{children as ReactElement<any, any>}</Slide>;
});

export default function CredencialModal({credencial, onClose}: ICredencialModal) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const ModalView: (props: IModalView) => JSX.Element =
        isMobile ? ModalMobileView : ModalDesktopView;

    return <Dialog open={true} onClose={onClose} fullScreen={isMobile}
                   TransitionComponent={Transition}
                   fullWidth={true} maxWidth={isMobile ? false : 'md'}>
        <ModalView onClose={onClose}>
            <ModalHabilitacaoView credencial={credencial}/>
        </ModalView>
    </Dialog>;
}

function ModalMobileView({onClose, children}: IModalView) {
    const classes = styles();
    return <>
        <div className="modal-header mobile">
            <h3>Credencial</h3>
            <button type="button" title="botão fechar" className="btn-close"
                    onClick={ev => onClose(ev, 'escapeKeyDown')}>
                <CloseIcon/>
            </button>
        </div>
        <DialogContent className={classes.appBarContent}>
            {children}
        </DialogContent>
    </>;
}

function ModalDesktopView({onClose, children}: IModalView) {
    const classes = styles();

    return <>
        <Toolbar className={classes.modalTitle}>
            <Typography variant="h6" className={classes.title}>
                Credencial
            </Typography>
            <IconButton edge="end" onClick={ev => onClose(ev, 'escapeKeyDown')} title="botão fechar modal">
                <CloseIcon className={classes.textLight}/>
            </IconButton>
        </Toolbar>
        <DialogContent className={classes.modalContent}>
            {children}
        </DialogContent>
    </>;
}

enum CredTipo {
    TITULAR,
    DEPENDENTE
}

function ModalHabilitacaoView({credencial}: { credencial: ICredResponse }) {
    const [expand, setExpand] = useState<CredTipo>(CredTipo.TITULAR);



    const handleChange = (tipo: CredTipo) => (ev: ChangeEvent<{}>, isExpanded: boolean) => {
        setExpand(isExpanded ? tipo : null);
    }

    return <>
        <Accordion expanded={expand === CredTipo.TITULAR}
                   onChange={handleChange(CredTipo.TITULAR)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Typography>Titular</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Box display="flex" flexDirection="row" flexWrap="wrap" justifyContent="center">
                    <SaldoCard credencial={credencial} />
                </Box>
                <Box display="flex" flexDirection="row" flexWrap="wrap" justifyContent="center">
                    <CredencialDiv credencial={credencial}/>
                </Box>
            </AccordionDetails>
        </Accordion>
        <ExtratoTable credencial={credencial} />
        <Accordion expanded={expand === CredTipo.DEPENDENTE}
                   onChange={handleChange(CredTipo.DEPENDENTE)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Typography>Dependentes</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Box display="flex" flexDirection="row" flexWrap="wrap" justifyContent="center" gap={2}>
                    {credencial.dependentes
                        .map((c, idx) =>
                            <CredencialDiv key={idx} credencial={c}/>)
                    }
                </Box>
            </AccordionDetails>
        </Accordion>
    </>;
}

function CredencialDiv({credencial}: { credencial: ICredencial }) {
    const refCredencial = useRef<HTMLDivElement>(null),
        refSvg = useRef<SVGSVGElement>(null);
    const
        [loading, setLoading] = useState(false),
        isIPhone = useMemo(() => isIos(), []);

    const downloadCredencial = useCallback(async () => {
        setLoading(true);
        toJpeg(refCredencial.current.firstChild as HTMLElement)
            .then(dataUrl => {
                download(dataUrl, `${credencial.nome}.jpeg`, 'image/jpeg');
                setLoading(false);
            });
    }, [refCredencial.current]);

    return <Box display="flex" flexDirection="column" alignItems="stretch" gap={1}>
        <Box display="flex" flexDirection="column" justifyContent="center"
             sx={{flex: {xs: "none", sm: 1}}} ref={refCredencial}>
            <Credencial refFoward={refSvg} credencial={credencial}/>
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center">
            <button
                style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    backgroundColor: '#1976d2', // Adjust the color to match your theme
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: loading ? 'none' : 'inline-block',
                }}
                disabled={loading}
                onClick={() => downloadCredencial()}
            >
                {loading ? 'Aguarde' : 'Baixar'}
            </button>
        </Box>
    </Box>
        ;
}